import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  fetchSchema,
  fetchBeliefs,
  createStory,
  updateStory,
} from "@/lib/api";
import { toast } from "sonner";
import { Search, X } from "lucide-react";

export function StoryEditor({ open, onClose, story, onSaved }) {
  const isEdit = !!story?.id;
  const [schema, setSchema] = useState(null);
  const [beliefs, setBeliefs] = useState([]);
  const [form, setForm] = useState({
    story_text: "",
    story_type: "",
    linked_belief_ids: [],
    source: "",
    freshness: "Evergreen",
    notes: "",
  });
  const [beliefQuery, setBeliefQuery] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchSchema().then(setSchema).catch(() => {});
    fetchBeliefs().then(setBeliefs).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (story) {
      setForm({
        story_text: story.story_text || "",
        story_type: story.story_type || "",
        linked_belief_ids: story.linked_belief_ids || [],
        source: story.source || "",
        freshness: story.freshness || "Evergreen",
        notes: story.notes || "",
      });
    } else {
      setForm({
        story_text: "",
        story_type: "",
        linked_belief_ids: [],
        source: "",
        freshness: "Evergreen",
        notes: "",
      });
    }
    setBeliefQuery("");
  }, [story, open]);

  const requireSource =
    form.story_type === "Non-fiction" || form.story_type === "Statistic";

  const beliefMap = useMemo(() => {
    const m = {};
    beliefs.forEach((b) => (m[b.id] = b));
    return m;
  }, [beliefs]);

  const filteredBeliefs = useMemo(() => {
    const q = beliefQuery.trim().toLowerCase();
    return beliefs
      .filter((b) => !q || b.belief_text.toLowerCase().includes(q))
      .slice(0, 20);
  }, [beliefs, beliefQuery]);

  const toggleLink = (id) => {
    setForm((f) => ({
      ...f,
      linked_belief_ids: f.linked_belief_ids.includes(id)
        ? f.linked_belief_ids.filter((x) => x !== id)
        : [...f.linked_belief_ids, id],
    }));
  };

  const save = async () => {
    if (!form.story_text.trim()) {
      toast.error("Story text is required");
      return;
    }
    if (requireSource && !form.source.trim()) {
      toast.error("Source is required for Non-fiction and Statistic stories");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        story_text: form.story_text.trim(),
        story_type: form.story_type || null,
        linked_belief_ids: form.linked_belief_ids,
        source: form.source || "",
        freshness: form.freshness,
        notes: form.notes || "",
      };
      const result = isEdit
        ? await updateStory(story.id, payload)
        : await createStory(payload);
      toast.success(isEdit ? "Story updated" : "Story created");
      onSaved?.(result);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-3xl bg-[#F2E9DB] border border-[#141B4D]/20 rounded-sm max-h-[90vh] overflow-y-auto"
        data-testid="story-editor-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#141B4D] text-xl font-bold tracking-tight">
            {isEdit ? "Edit story" : "New story"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Story text *
            </label>
            <textarea
              data-testid="story-text-input"
              value={form.story_text}
              onChange={(e) => setForm({ ...form, story_text: e.target.value })}
              rows={6}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Story type
              </label>
              <select
                data-testid="story-type-select"
                value={form.story_type}
                onChange={(e) =>
                  setForm({ ...form, story_type: e.target.value })
                }
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              >
                <option value="">—</option>
                {(schema?.story_types || []).map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
                Freshness
              </label>
              <select
                data-testid="story-freshness-select"
                value={form.freshness}
                onChange={(e) =>
                  setForm({ ...form, freshness: e.target.value })
                }
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              >
                {(schema?.freshness_values || ["Evergreen"]).map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Source {requireSource && <span className="text-rose-600">*</span>}
            </label>
            <input
              data-testid="story-source-input"
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Linked beliefs ({form.linked_belief_ids.length} selected)
            </label>
            {form.linked_belief_ids.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-3">
                {form.linked_belief_ids.map((id) => {
                  const b = beliefMap[id];
                  if (!b) return null;
                  return (
                    <span
                      key={id}
                      data-testid={`story-linked-belief-${id}`}
                      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs bg-[#141B4D] text-[#F2E9DB]"
                    >
                      <span className="max-w-[260px] truncate">
                        {b.belief_text}
                      </span>
                      <button
                        type="button"
                        onClick={() => toggleLink(id)}
                        className="hover:opacity-70"
                        data-testid={`story-unlink-belief-${id}`}
                      >
                        <X size={12} />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#141B4D]/50"
              />
              <input
                data-testid="story-belief-search"
                value={beliefQuery}
                onChange={(e) => setBeliefQuery(e.target.value)}
                placeholder="Search beliefs to link…"
                className="w-full bg-white border border-[#141B4D]/20 rounded-sm pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
              />
            </div>
            <div className="mt-2 max-h-48 overflow-y-auto border border-[#141B4D]/15 rounded-sm bg-white">
              {filteredBeliefs.length === 0 ? (
                <div className="px-3 py-3 text-xs text-[#141B4D]/50">
                  No beliefs match.
                </div>
              ) : (
                filteredBeliefs.map((b) => {
                  const active = form.linked_belief_ids.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleLink(b.id)}
                      data-testid={`story-link-option-${b.id}`}
                      className={`w-full text-left px-3 py-2 text-xs border-b border-[#141B4D]/10 hover:bg-[#141B4D]/5 ${
                        active ? "bg-[#FFD100]/30" : ""
                      }`}
                    >
                      <span className="text-[#141B4D]">{b.belief_text}</span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          <div>
            <label className="text-xs uppercase tracking-wider text-[#141B4D]/60 block mb-2">
              Notes
            </label>
            <textarea
              data-testid="story-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full bg-white border border-[#141B4D]/20 rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FFD100]"
            />
          </div>
        </div>
        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            data-testid="story-cancel-button"
            className="px-5 py-2 rounded-sm text-sm border border-[#141B4D]/20 text-[#141B4D] hover:bg-[#141B4D]/5"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="story-save-button"
            className="px-5 py-2 rounded-sm text-sm bg-[#141B4D] text-[#F2E9DB] font-medium hover:bg-[#141B4D]/90 disabled:opacity-60"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create story"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
